import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { AlertTriangle, Check, SlidersHorizontal, Plus, X } from "lucide-react";
import { api, formatApiError, CLASS_STYLE, PRIO_STYLE, PRIORITY_LABELS, IMPEDIMENT_KEY } from "@/lib/api";
import { toast } from "sonner";

export default function KanbanBoard({ projectId, refreshKey, onOpenTask, onChanged }) {
  const [board, setBoard] = useState(null);
  const [wipEdit, setWipEdit] = useState(null);     // { id, wip }
  const [reasonFor, setReasonFor] = useState(null); // { taskId, columnId, order, reason }
  const [disabling, setDisabling] = useState(null);  // { moveTo }
  const [enabling, setEnabling] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/projects/${projectId}/board`); setBoard(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, refreshKey]);

  if (!board) return <div className="p-8 text-center text-sm text-slate-500">Carregando quadro...</div>;
  const { columns, cards, can_admin, can_move } = board;
  const hasBlocked = columns.some((c) => c.id === IMPEDIMENT_KEY);

  const doMove = async (taskId, columnId, order, { reason, force } = {}) => {
    try {
      await api.post(`/tasks/${taskId}/move`, { column_id: columnId, order, reason, force_wip_exception: force });
      onChanged?.(); load();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (typeof detail === "string" && detail.includes("WIP")) {
        if (window.confirm(`${detail}\n\nAutorizar esta movimentação como exceção?`)) return doMove(taskId, columnId, order, { reason, force: true });
      } else {
        toast.error(formatApiError(e));
      }
      load();
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;
    if (destination.droppableId === IMPEDIMENT_KEY) {
      setReasonFor({ taskId: draggableId, columnId: IMPEDIMENT_KEY, order: destination.index, reason: "" });
      return;
    }
    // optimistic update
    const next = { ...cards };
    const srcList = [...(next[source.droppableId] || [])];
    const [moved] = srcList.splice(source.index, 1);
    const dstList = source.droppableId === destination.droppableId ? srcList : [...(next[destination.droppableId] || [])];
    dstList.splice(destination.index, 0, { ...moved, board_column_id: destination.droppableId });
    next[source.droppableId] = srcList; next[destination.droppableId] = dstList;
    setBoard({ ...board, cards: next });
    doMove(draggableId, destination.droppableId, destination.index);
  };

  const confirmReason = async () => {
    if (!reasonFor.reason.trim()) { toast.error("Informe o motivo do impedimento."); return; }
    await doMove(reasonFor.taskId, reasonFor.columnId, reasonFor.order, { reason: reasonFor.reason.trim() });
    setReasonFor(null);
  };

  const saveWip = async () => {
    try { await api.patch(`/projects/${projectId}/columns/${wipEdit.id}`, { wip_limit: wipEdit.wip === "" ? 0 : Number(wipEdit.wip) }); setWipEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const enableImpediment = async () => {
    try { await api.post(`/projects/${projectId}/impediment-column`, {}); setEnabling(false); toast.success("Coluna Impedimento/Problema ativada."); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const disableImpediment = async () => {
    try {
      const params = disabling.moveTo ? { move_to: disabling.moveTo } : {};
      await api.delete(`/projects/${projectId}/impediment-column`, { params });
      setDisabling(null); toast.success("Coluna desativada."); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const startDisable = () => {
    const blockedCards = (cards[IMPEDIMENT_KEY] || []).length > 0;
    setDisabling({ moveTo: blockedCards ? "todo" : "", hasCards: blockedCards });
  };

  return (
    <div data-testid="kanban-board">
      {can_admin && (
        <div className="mb-3 flex justify-end">
          {hasBlocked ? (
            <button onClick={startDisable} data-testid="disable-impediment-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><X className="w-4 h-4" /> Desativar Impedimento/Problema</button>
          ) : (
            <button onClick={enableImpediment} data-testid="enable-impediment-btn" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-amber-200 text-sm text-amber-700 hover:bg-amber-50"><Plus className="w-4 h-4" /> Ativar coluna Impedimento/Problema</button>
          )}
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-columns">
          {columns.map((col) => {
            const list = cards[col.id] || [];
            const isBlocked = col.id === IMPEDIMENT_KEY;
            const overWip = col.wip_limit && list.length > col.wip_limit;
            const atWip = col.wip_limit && list.length >= col.wip_limit;
            return (
              <div key={col.id} className={`w-72 shrink-0 rounded-xl p-2 ${isBlocked ? "bg-amber-50/70" : "bg-slate-100/70"}`} data-testid={`kanban-column-${col.id}`}>
                <div className="flex items-center justify-between px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    {isBlocked && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                    <span className="font-medium text-sm text-slate-800 truncate">{col.name}</span>
                    <span className={`text-xs px-1.5 rounded ${overWip ? "bg-red-100 text-red-700" : atWip ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`} data-testid={`col-count-${col.id}`}>
                      {list.length}{col.wip_limit ? `/${col.wip_limit}` : ""}
                    </span>
                  </div>
                  {can_admin && (
                    <button onClick={() => setWipEdit({ id: col.id, wip: col.wip_limit || "" })} data-testid={`col-wip-edit-${col.id}`} className="p-1 rounded hover:bg-slate-200 text-slate-500" title="Definir limite WIP"><SlidersHorizontal className="w-3.5 h-3.5" /></button>
                  )}
                </div>

                {wipEdit?.id === col.id && (
                  <div className="px-2 py-2 mb-2 bg-white rounded-lg border border-slate-200 space-y-2" data-testid={`col-wip-form-${col.id}`}>
                    <label className="block text-xs text-slate-500">Limite WIP (0 = sem limite)</label>
                    <input type="number" min="0" autoFocus value={wipEdit.wip} onChange={(e) => setWipEdit({ ...wipEdit, wip: e.target.value })} data-testid={`col-wip-${col.id}`} className="w-full h-8 rounded-md border border-slate-200 px-2 text-sm" />
                    <div className="flex gap-2"><button onClick={saveWip} data-testid={`col-wip-save-${col.id}`} className="flex-1 h-8 rounded-md bg-slate-900 text-white text-xs"><Check className="w-3.5 h-3.5 inline" /> Salvar</button><button onClick={() => setWipEdit(null)} className="h-8 px-2 rounded-md border border-slate-200 text-xs">Cancelar</button></div>
                  </div>
                )}

                <Droppable droppableId={col.id} isDropDisabled={!can_move}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className={`min-h-[60px] space-y-2 p-1 rounded-lg transition-colors ${snapshot.isDraggingOver ? "bg-slate-200/60" : ""}`}>
                      {list.map((t, i) => (
                        <Draggable key={t.id} draggableId={t.id} index={i} isDragDisabled={!can_move}>
                          {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}
                              onClick={() => onOpenTask(t.id)} data-testid={`kanban-card-${t.id}`}
                              className={`bg-white rounded-lg border p-3 cursor-pointer shadow-sm hover:shadow ${t.impediment_active ? "border-amber-300" : "border-slate-200"} ${snap.isDragging ? "ring-2 ring-slate-400" : ""}`}
                              style={{ ...prov.draggableProps.style, borderLeft: t.sprint_color ? `4px solid ${t.sprint_color}` : undefined }}>
                              {t.impediment_active && (
                                <div className="flex items-center gap-1 text-[10px] font-semibold text-amber-700 mb-1"><AlertTriangle className="w-3 h-3" /> Impedida</div>
                              )}
                              <div className="text-sm font-medium text-slate-900 line-clamp-2">{t.title}</div>
                              <div className="flex items-center gap-1 flex-wrap mt-2">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${CLASS_STYLE[t.classification]}`}>{t.classification}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIO_STYLE[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                                {t.sprint_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.sprint_name}</span>}
                                {t.story_points != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.story_points} SP</span>}
                              </div>
                              <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                                <span className="truncate">{t.assignee_name || "Sem responsável"}</span>
                                {t.due_date && <span>{new Date(t.due_date).toLocaleDateString("pt-BR")}</span>}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {reasonFor && (
        <div className="fixed inset-0 z-[60] bg-slate-900/40 grid place-items-center p-4" onClick={() => { setReasonFor(null); load(); }} data-testid="impediment-reason-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Registrar impedimento</h3>
            <p className="text-sm text-slate-500">Descreva o motivo do bloqueio. O último status operacional será preservado.</p>
            <textarea autoFocus rows={3} value={reasonFor.reason} onChange={(e) => setReasonFor({ ...reasonFor, reason: e.target.value })} data-testid="impediment-reason-input" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Motivo do impedimento (obrigatório)" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setReasonFor(null); load(); }} className="h-9 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button onClick={confirmReason} data-testid="impediment-reason-confirm" className="h-9 px-4 rounded-lg bg-amber-600 text-white text-sm font-medium">Confirmar impedimento</button>
            </div>
          </div>
        </div>
      )}

      {disabling && (
        <div className="fixed inset-0 z-[60] bg-slate-900/40 grid place-items-center p-4" onClick={() => setDisabling(null)} data-testid="disable-impediment-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold">Desativar Impedimento/Problema</h3>
            {disabling.hasCards ? (
              <>
                <p className="text-sm text-slate-500">Há tarefas impedidas. Escolha uma coluna padrão de destino.</p>
                <select value={disabling.moveTo} onChange={(e) => setDisabling({ ...disabling, moveTo: e.target.value })} data-testid="disable-impediment-dest" className="prop-select">
                  {columns.filter((c) => c.id !== IMPEDIMENT_KEY).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            ) : <p className="text-sm text-slate-500">A coluna está vazia e será removida.</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDisabling(null)} className="h-9 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button onClick={disableImpediment} disabled={disabling.hasCards && !disabling.moveTo} data-testid="disable-impediment-confirm" className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50">Desativar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
